import { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useHistory, useParams } from 'react-router-dom';
import { Button, Card, Notice, SelectField, TextField } from '@tcrd/ui';
import { Page } from '../../../components/Page';
import { useToast } from '../../../components/toast';
import { api, errorMessage } from '../../../lib/api';
import type { VehicleDto } from '../vehicles/types';
import type { DocumentDto, DocumentType } from './types';

interface FormValues {
  documentTypeId: string;
  vehicleId: string;
  number: string;
  category: string;
  issuer: string;
  issuedAt: string;
  startsAt: string;
  expiresAt: string;
}

export function DocumentFormPage() {
  const { id } = useParams<{ id?: string }>();
  const editing = !!id && id !== 'new';
  const history = useHistory();
  const qc = useQueryClient();
  const toast = useToast();
  const types = useQuery({ queryKey: ['document-types'], queryFn: () => api<DocumentType[]>('/documents/types') });
  const vehicles = useQuery({ queryKey: ['vehicles'], queryFn: () => api<VehicleDto[]>('/vehicles') });
  const existing = useQuery({ queryKey: ['document', id], queryFn: () => api<DocumentDto>(`/documents/${id}`), enabled: editing });
  const { register, handleSubmit, watch, reset, formState } = useForm<FormValues>();

  useEffect(() => {
    const d = existing.data;
    if (d)
      reset({
        documentTypeId: d.type.id,
        vehicleId: d.vehicle?.id ?? '',
        number: d.number ?? '',
        category: d.category ?? '',
        issuer: d.issuer ?? '',
        issuedAt: d.issuedAt ?? '',
        startsAt: d.startsAt ?? '',
        expiresAt: d.expiresAt ?? '',
      });
  }, [existing.data, reset]);

  const type = types.data?.find((t) => t.id === watch('documentTypeId'));
  const isLicense = type?.code === 'DRIVER_LICENSE';
  const isInsurance = type?.code === 'VEHICLE_INSURANCE';

  const onSubmit = handleSubmit(async (v) => {
    const fields = {
      number: v.number || undefined,
      category: v.category || undefined,
      issuer: v.issuer || undefined,
      issuedAt: v.issuedAt || undefined,
      startsAt: v.startsAt || undefined,
      expiresAt: v.expiresAt || undefined,
    };
    try {
      const saved = editing
        ? await api<DocumentDto>(`/documents/${id}`, { method: 'PATCH', body: fields })
        : await api<DocumentDto>('/documents', { body: { ...fields, documentTypeId: v.documentTypeId, vehicleId: type?.subject === 'VEHICLE' ? v.vehicleId : undefined } });
      await qc.invalidateQueries({ queryKey: ['documents'] });
      await qc.invalidateQueries({ queryKey: ['compliance'] });
      toast(editing ? 'Documento actualizado: requiere nueva verificación' : 'Documento registrado. Ahora sube las imágenes.', 'success');
      history.replace(`/driver/documents/${saved.id}`);
    } catch (e) {
      toast(errorMessage(e), 'danger');
    }
  });

  return (
    <Page title={editing ? 'Editar documento' : 'Nuevo documento'} back="/driver/documents">
      <form onSubmit={onSubmit} className="flex flex-col gap-4">
        {editing && <Notice tone="warning">Al modificar los datos, el documento vuelve a quedar pendiente de verificación.</Notice>}
        <Card className="p-5 flex flex-col gap-4">
          <SelectField label="Tipo de documento" disabled={editing} {...register('documentTypeId', { required: true })}>
            <option value="">Selecciona…</option>
            {types.data?.map((t) => (
              <option key={t.id} value={t.id}>{t.name}{t.mandatory ? ' (obligatorio)' : ''}</option>
            ))}
          </SelectField>
          {type?.subject === 'VEHICLE' && !editing && (
            <SelectField label="Vehículo" {...register('vehicleId', { required: true })}>
              <option value="">Selecciona…</option>
              {vehicles.data?.map((v) => <option key={v.id} value={v.id}>{v.make} {v.model} · {v.plate}</option>)}
            </SelectField>
          )}
          <TextField label={isInsurance ? 'Número de póliza' : 'Número'} {...register('number')} />
          {isLicense && <TextField label="Categoría" hint="Ej. 2, 3, 4" {...register('category')} />}
          {isInsurance && <TextField label="Aseguradora" {...register('issuer')} />}
          <div className="grid grid-cols-2 gap-3">
            {isInsurance ? <TextField label="Inicio de vigencia" type="date" {...register('startsAt')} /> : <TextField label="Fecha de expedición" type="date" {...register('issuedAt')} />}
            <TextField label="Vencimiento" type="date" {...register('expiresAt', { required: type?.requiresExpiry })} />
          </div>
        </Card>
        <Button type="submit" size="lg" loading={formState.isSubmitting}>{editing ? 'Guardar' : 'Continuar'}</Button>
      </form>
    </Page>
  );
}
